/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SignInButtonWrapper, Wrapper } from './OChatSignedOut.styles';
import { OChatSignedOutProps, OChatSignedOutTextBlockSimple } from './OChatSignedOut.types';
import {
  ABodySize,
  AHeaderSize,
  AHeaderTag,
  MButton,
  MButtonIconPlacement,
  MButtonSize,
  MButtonType,
  MTextBlockSimple,
  MTextBlockSimpleAlignment,
} from '@marriott/mi-web-ui-kit';
import { SignInDrawerWrapper, NavigationContext } from '@marriott/mi-common-static-components';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { useAISearchJoinOrLoginPageView, useGlobalLoginPageView } from '../../hooks/useAISearchPageView';
import { useSignInDrawerModel } from '../../store';
import { useGlobalStore, useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { usePageContext } from '@marriott/mi-shop-components';
import { ACCOUNT_FLYOUT_MODAL_ID, ACCOUNT_MODAL_OPEN_EVENT } from '@marriott/shared/mi-helper-utils';
import sparkleIconOffline from '../ONavigationSide/sparkle.png';
import aiLoggedOutIcon from './ai_logged_out.png';

interface StaticImageData {
  src: string;
  height: number;
  width: number;
  blurDataURL?: string;
}

const getSparkleIconSrc = (img: string | StaticImageData): string =>
  typeof img === 'string' ? img : (img as StaticImageData).src;

const JOIN_FOR_FREE_TEXT = 'Join for free';
const FLYOUT_OPEN_REPLAY_DELAY_MS = 180;

function dispatchGlobalHeaderSignInFlyout(isRememberedUser: boolean): void {
  document.dispatchEvent(
    new CustomEvent(ACCOUNT_MODAL_OPEN_EVENT, {
      detail: {
        modalType: ACCOUNT_FLYOUT_MODAL_ID,
        isSwap: !isRememberedUser,
        isFlyOut: true,
        isGlobalHeaderSignIn: true,
      },
    })
  );
}

function renderTextBlock(block: OChatSignedOutTextBlockSimple | undefined): JSX.Element | null {
  if (!block?.aHeader && !block?.aBody) return null;
  return (
    <div className="o-chat-signed-out-text-block">
      <MTextBlockSimple
        alignment={MTextBlockSimpleAlignment.Center}
        bodyText={block.aBody?.bodyText}
        bodySize={block.aBody?.size as ABodySize}
        eyebrowText={block.aEyebrow?.eyebrowText as string}
        headerText={block.aHeader?.text}
        headerSize={block.aHeader?.size as AHeaderSize}
        headerTag={block.aHeader?.headerTag as AHeaderTag}
      />
    </div>
  );
}

export function OChatSignedOutComp(props: OChatSignedOutProps): JSX.Element {
  const [isSignInDrawerOpen, setIsSignInDrawerOpen] = useState(false);
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
  const [isMyTripsModalOpen, setIsMyTripsModalOpen] = useState(false);
  const { mTextBlockSimple, mButton, className, style } = props;
  const { isOffline = false } = usePageContext();
  const sparkleIconSrc = isOffline ? getSparkleIconSrc(sparkleIconOffline) : getSparkleIconSrc(aiLoggedOutIcon);
  const sparkleImageWidth = isOffline ? 100 : 233;
  const sparkleImageHeight: number | 'auto' = isOffline ? 100 : 'auto';

  const rememberedUser = useUserDetailsGlobalStore(state => state.rememberedUser);
  const isSignedInUser = useUserDetailsGlobalStore(state => state.isSignedInUser);
  const displayName = useUserDetailsGlobalStore(state => state.name);
  const isUserDetailsSaved = useUserDetailsGlobalStore(state => state.isUserDetailsSaved);
  const isAccountFlyoutEnabled = useGlobalStore(state => state.isAccountFlyoutEnabled);

  const isRememberedUser = Boolean(rememberedUser && !isSignedInUser && !isOffline);
  const firstName = displayName?.trim().split(/\s+/).filter(Boolean)[0] ?? 'there';

  const signInDrawerModel = useSignInDrawerModel();
  const flyoutAutoOpenDoneRef = useRef(false);
  const hasSignInModel = Boolean(signInDrawerModel);
  const shouldRenderRememberedSignIn = !isOffline && hasSignInModel && isRememberedUser;
  const shouldRenderAnonymousSignIn = !isOffline && hasSignInModel && !isRememberedUser;

  const openSignIn = useCallback(() => {
    if (isAccountFlyoutEnabled) {
      setIsSignInDrawerOpen(true);
      dispatchGlobalHeaderSignInFlyout(isRememberedUser);
      return;
    }
    setIsSignInDrawerOpen(true);
  }, [isAccountFlyoutEnabled, isRememberedUser]);

  /** Reset flyout auto-open flag on mount to ensure fresh state after sign-out reload. */
  useEffect(() => {
    flyoutAutoOpenDoneRef.current = false;
  }, []);

  /** Open sign-in on load after user details are fetched. */
  useEffect(() => {
    let flyoutAutoOpenClear: (() => void) | undefined;

    if (!isUserDetailsSaved) {
      flyoutAutoOpenDoneRef.current = false;
    } else if (!isOffline) {
      if (isAccountFlyoutEnabled) {
        if (!flyoutAutoOpenDoneRef.current) {
          const openTimeoutId = window.setTimeout(() => {
            flyoutAutoOpenDoneRef.current = true;
            dispatchGlobalHeaderSignInFlyout(isRememberedUser);
            flyoutAutoOpenDoneRef.current = false;
          }, FLYOUT_OPEN_REPLAY_DELAY_MS);
          flyoutAutoOpenClear = () => window.clearTimeout(openTimeoutId);
        }
      } else if (signInDrawerModel) {
        setIsSignInDrawerOpen(true);
      }
    }

    return flyoutAutoOpenClear;
  }, [isUserDetailsSaved, isOffline, isAccountFlyoutEnabled, signInDrawerModel, isRememberedUser]);

  /**
   * ACDL PAGEVIEW Tracking for "AI Search|Join or Login"
   * Fires when unauthenticated or remembered users see the sign-in prompt
   * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/3209003032
   */
  useAISearchJoinOrLoginPageView(true);

  /**
   * ACDL PAGEVIEW Tracking for "Global Login" modal
   * Fires when the sign-in drawer is opened
   */
  const { trackPageView: trackGlobalLoginPageView } = useGlobalLoginPageView(false);

  /**
   * Track Global Login modal PAGEVIEW when sign-in drawer opens
   */
  const handleSignInDrawerOpen = useCallback(() => {
    trackGlobalLoginPageView();
  }, [trackGlobalLoginPageView]);

  /**
   * Track Global Login modal when drawer state changes to open
   */
  useEffect(() => {
    if (isSignInDrawerOpen) {
      handleSignInDrawerOpen();
    }
  }, [isSignInDrawerOpen, handleSignInDrawerOpen]);

  const renderAnonymousUtilityLink = useCallback(
    (_restProps: unknown, _buttonClass: string, buttonText?: string) => (
      <span>{buttonText ?? mButton?.buttonText ?? 'Join or Sign in'}</span>
    ),
    [mButton?.buttonText]
  );

  /** Same `SignInDrawer` trigger as anonymous flow; label only differs. */
  const renderRememberedUtilityLink = useCallback(
    (_restProps: unknown, _buttonClass: string, _buttonText?: string) => <span>Sign In</span>,
    []
  );

  const navigationContextValue = useMemo(
    () => ({
      offset: 0,
      navRef: null,
      isInverse: false,
      isSignInDrawerOpen,
      setIsSignInDrawerOpen,
      isMainMenuOpen,
      setIsMainMenuOpen,
      isMyTripsModalOpen,
      setIsMyTripsModalOpen,
      isAccountLoggedInPanelEnabled: false,
      setIsAccountLoggedInPanelEnabled: () => {},
    }),
    [isSignInDrawerOpen, isMainMenuOpen, isMyTripsModalOpen]
  );

  const signInDrawerTree = (renderUtilityLink: typeof renderAnonymousUtilityLink) => (
    <SignInButtonWrapper>
      <NavigationContext.Provider value={navigationContextValue}>
        <SignInDrawerWrapper
          logoUrl={''}
          logoImageAltText={''}
          logoType={''}
          ctaClass="m-button-m"
          restProps={signInDrawerModel}
          isAuthorMode={false}
          isBrandedVariation={false}
          renderUtilityLink={renderUtilityLink}
        />
      </NavigationContext.Provider>
    </SignInButtonWrapper>
  );

  const heroImage = (
    <div className="o-chat-signed-out-icon d-flex justify-content-center">
      <img src={sparkleIconSrc} alt="" width={sparkleImageWidth} height={sparkleImageHeight} aria-hidden="true" />
    </div>
  );

  /** no drawer until AEM model exists. */
  if (!hasSignInModel) {
    return (
      <Wrapper className={className} style={style}>
        <div className="o-chat-signed-out-container col-12 col-lg-8 p-0">
          {heroImage}
          {renderTextBlock(mTextBlockSimple)}
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className} style={style}>
      <div className="o-chat-signed-out-container col-12 col-lg-8 p-0">
        {heroImage}
        {renderTextBlock(mTextBlockSimple)}
        {shouldRenderRememberedSignIn && (
          <>
            {signInDrawerTree(renderRememberedUtilityLink)}
            <div className="o-chat-signed-out-remembered-footer d-flex align-items-center justify-content-center">
              <span className="o-chat-signed-out-remembered-label t-font-s">Not {firstName}?</span>
              <MButton
                type="button"
                className="o-chat-signed-out-remembered-join"
                buttonType={MButtonType.Tertiary}
                size={MButtonSize.S}
                iconPlacement={MButtonIconPlacement.Trailing}
                buttonText={JOIN_FOR_FREE_TEXT}
                onClick={openSignIn}
              />
            </div>
          </>
        )}
        {shouldRenderAnonymousSignIn ? signInDrawerTree(renderAnonymousUtilityLink) : null}
      </div>
    </Wrapper>
  );
}

export const OChatSignedOutConfig = {
  emptyLabel: 'OChatSignedOut',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/o-chat-signed-out`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OChatSignedOut = (props: any) => {
  return (
    <EditableComponent config={OChatSignedOutConfig} {...props}>
      <OChatSignedOutComp {...props.model} />
    </EditableComponent>
  );
};

export * from './OChatSignedOut.types';
